import { ReactNode, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { Header } from './Header';
import { Menu, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { AIChatPanel } from '@/components/AIAssistant';
import { useNavigation } from '@/contexts/NavigationContext';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isAIChatOpen, setIsAIChatOpen] = useState(false);
  const [aiInitialPrompt, setAiInitialPrompt] = useState<string | undefined>();
  const location = useLocation();
  const [displayLocation, setDisplayLocation] = useState(location);
  const [transitionStage, setTransitionStage] = useState('fadeIn');
  const { sectionName } = useNavigation();

  const handleAIPromptSubmit = (prompt: string) => {
    setAiInitialPrompt(prompt);
    setIsAIChatOpen(true);
  };

  useEffect(() => {
    if (location !== displayLocation) {
      setTransitionStage('fadeOut');
    }
  }, [location, displayLocation]);

  const onAnimationEnd = () => {
    if (transitionStage === 'fadeOut') {
      setTransitionStage('fadeIn');
      setDisplayLocation(location);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Header - Does NOT adjust with chat panel */}
      <div 
        className={cn(
          "hidden lg:block fixed top-0 z-30",
          "left-64 right-0"
        )}
      >
        <Header onAIPromptSubmit={handleAIPromptSubmit} />
      </div>

      {/* Mobile Header - Shows only on mobile when sidebar is hidden */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-30 h-16 bg-neutral-950 border-b border-neutral-800 flex items-center justify-between px-4">
        <div className="flex items-center">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2 text-neutral-400 hover:text-white transition-colors"
          >
            <Menu size={24} />
          </button>
          <div className="flex items-center space-x-2 ml-4">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-gradient-to-r from-primary-500 to-cyan-500">
              <span className="text-sm font-bold text-black">L4</span>
            </div>
            <div>
              <span className="text-lg font-semibold text-white">Line4</span>
              <span className="ml-1 text-sm text-neutral-400">.Observability</span>
            </div>
          </div>
        </div>
      </div>

      {/* Layout Container */}
      <div className="flex h-screen">
        {/* Sidebar */}
        <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {/* Main Content Area - Shrinks when chat is open */}
        <div 
          className={cn(
            "flex-1 flex flex-col overflow-hidden transition-all duration-300",
            isAIChatOpen ? "lg:mr-96" : "mr-0"
          )}
        >
          {/* Content */}
          <main 
            className={cn(
              "flex-1 overflow-y-auto bg-background",
              "pt-16 lg:pt-16"
            )}
          >
            <div 
              className={cn(
                "container mx-auto px-4 py-8",
                transitionStage === 'fadeIn' ? 'animate-fadeIn' : 'animate-fadeOut'
              )}
              onAnimationEnd={onAnimationEnd}
            >
              {children}
            </div>
          </main>
        </div>

        {/* Global AI Chat Panel - Slides in from right below header */}
        <div 
          className={cn(
            "fixed right-0 w-96 bg-neutral-950 border-l border-neutral-800 z-40",
            "flex flex-col shadow-2xl",
            "transition-transform duration-300 ease-out",
            "top-16 bottom-0", // Below the 64px header
            isAIChatOpen ? "translate-x-0" : "translate-x-full"
          )}
        >
          <AIChatPanel
            isOpen={isAIChatOpen}
            onClose={() => {
              setIsAIChatOpen(false);
              setAiInitialPrompt(undefined);
            }}
            initialPrompt={aiInitialPrompt}
            context={sectionName || 'Dashboard'}
          />
        </div>
      </div>

      {/* Floating Themis Button */}
      <button
        onClick={() => setIsAIChatOpen(true)}
        className={cn(
          "fixed bottom-6 right-6 z-40",
          "w-14 h-14 rounded-full",
          "bg-gradient-to-r from-primary-500 to-cyan-500",
          "text-black shadow-lg shadow-primary-500/50",
          "hover:shadow-xl hover:shadow-primary-500/60",
          "transition-all duration-200",
          "flex items-center justify-center",
          "group",
          isAIChatOpen && "scale-0 opacity-0 pointer-events-none"
        )}
        aria-label="Open Themis"
      >
        <Sparkles className="w-6 h-6 group-hover:scale-110 transition-transform" />
      </button>
    </div>
  );
}